import { computed, type Ref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { getWorkspaceConnections } from '@/modules/workspace/api'
import { session } from '@/composables/useSession'

/**
 * Checks if a workspace has any connections configured
 * Used to disable assignment creation/editing when workspace has no connections
 *
 * @param workspaceId - The workspace ID to check (defaults to session workspace)
 */
export const useWorkspaceConnections = (workspaceId?: Ref<number> | number) => {
  const targetWorkspaceId = computed(() =>
    typeof workspaceId === 'number'
      ? workspaceId
      : workspaceId?.value ?? session.workspace.id
  )

  const { data: connections, isFetched } = useQuery({
    queryKey: ['workspace-connections', targetWorkspaceId],
    queryFn: () => getWorkspaceConnections(targetWorkspaceId.value),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: false,
  })

  const hasConnections = computed(() => {
    if (!isFetched.value) return false // Fail closed: assume no connections until verified
    return !!connections.value?.length
  })

  return {
    connections,
    hasConnections,
    isFetched,
  }
}
